import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { Form, Field } from 'react-final-form';
import {
  Button,
  InputField,
} from '@unite-us/ui';
import { validations, Icon } from '@unite-us/app-components';
import { useInvalidateQueries } from 'src/api/APIHooks';
import composeValidators from 'src/components/Organization/utils/composeValidators';
import { browserHistory } from 'common/utils/browserHistory';
import { BackButton } from 'common/buttons';
import { Spinner } from 'common/spinners';
import { env } from 'src/config/env';
import { connect } from 'react-redux';
import { hint2340AllowUsersToDeactivateOldPrds } from 'src/common/utils/FeatureFlags/flags';
import Notifier from 'common/helpers/Notifier';
import NetworkTypeaheadField from './NetworksTypeaheadField';
import { ActivationModal } from './ActivationModal';
import { useUpdateDirectory } from '../../../../components/Backoffice/api/hooks/prdApi/DirectoryHooks';

const namespace = 'prd-editor-form';

export const PrdForm = ({
  prdId, onSubmit, initialValues, showPending, isDeactivationEnabled,
}) => {
  const isEditing = !isEmpty(prdId);
  const modalRef = useRef(null);
  const title = isEditing ? 'Edit PRD' : 'Create New PRD';
  const disabled = initialValues.disabled || false;
  const invalidateQueries = useInvalidateQueries();

  const onCancel = useCallback(() => {
    if (isEditing) {
      browserHistory.push({ pathname: `/backoffice/prds/${prdId}` });
    } else {
      browserHistory.push({ pathname: '/backoffice/prds' });
    }
  }, [prdId, isEditing]);

  const { updateRecord: updatePrd } = useUpdateDirectory({
    onError: (error) => {
      const action = disabled ? 'reactivation' : 'deactivation';
      const message = `PRD ${action} unsuccessful due to ${error.response.status} error. Please try again.`;
      Notifier.dispatch(error.response.status, message);
    },
    onSuccess: () => {
      invalidateQueries('directories');
      Notifier.dispatch(200, `PRD is successfully ${disabled ? 'reactivated' : 'deactivated'}.`);
    },
  });

  return (
    <div id="prd_form" className="bg-dark-fill-blue">
      <div className="flex p-4 items-center bg-white">
        <BackButton navigateTo={onCancel} />
        <h1 className="font-bold flex-1 text-center">{title}</h1>
        {isDeactivationEnabled && isEditing && (
          <>
            <Button
              data-test-element="enable-prd-btn"
              aria-label={disabled ? 'Reactivate PRD' : 'Deactivate PRD'}
              label={disabled ?
                (
                  <div className="flex items-center gap-x-2">
                    <Icon
                      icon="Refresh"
                    />
                    <span className="text-[#4467AB]">REACTIVATE</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-x-2">
                    <Icon
                      icon="CrossCircle"
                    />
                    <span className="text-[#4467AB]">DEACTIVATE</span>
                  </div>
                )}
              style={{
                border: '1px solid rgba(68, 103, 171, 1)',
              }}
              omitDefaultStyles
              onClick={() => {
                if (modalRef.current) {
                  modalRef.current.openModal();
                }
              }}
            />
            <ActivationModal
              activate={disabled}
              ref={modalRef}
              customerName={initialValues.customer_name}
              domain={initialValues.domain}
              confirmationCallback={() => {
                updatePrd(prdId, { disabled: !disabled });
              }}
            />
          </>
        )}
      </div>
      {showPending && <Spinner />}
      {!showPending && (
        <div className="p-6">
          <Form
            initialValues={initialValues}
            onSubmit={onSubmit}
            render={({ handleSubmit }) => (
              <form
                onSubmit={handleSubmit}
                data-testid="prd-form"
                className={
                  'grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-12 ' +
                  'pl-10 pt-6 bg-light-border-grey border border-solid rounded overflow-hidden border-dark-border-blue'
                }
              >
                <Field
                  name="customer_name"
                  validate={composeValidators(validations.isRequired)}
                >
                  {(props) => (
                    <InputField
                      className="md:col-span-6 xl:col-span-6 px-0"
                      id={`${namespace}__customer_name-field`}
                      label="Customer Name"
                      required
                      placeholder="Type Customer Name"
                      {...props}
                    />
                  )}
                </Field>
                <div className="md:col-span-6 px-0" />
                <Field
                  name="domain"
                  validate={(value) => {
                    if (value) {
                      if (/^-|-$/.test(value)) {
                        return 'Domain cannot start or end with a hyphen';
                      }
                      if (/[^a-zA-Z0-9-]/.test(value)) {
                        return 'Domain cannot contain special characters';
                      }
                      if (value.length > 63) {
                        return 'Domain cannot be longer than 63 characters';
                      }
                    }
                    return validations.isRequired(value);
                  }}
                >
                  {(props) => (
                    <>
                      <InputField
                        className="md:col-span-4 xl:col-span-4 px-0"
                        id={`${namespace}__domain-field`}
                        label="Domain Name"
                        required
                        placeholder="Type Domain Name"
                        {...props}
                      />
                      <div className="md:col-span-2 xl:col-span-2 px-0 pt-8">
                        <span>.{env.PRD_FE_DOMAIN}</span>
                      </div>
                    </>

                  )}
                </Field>
                <div className="md:col-span-6 px-0" />
                <Field
                  name="customer_code"
                >
                  {(props) => (
                    <InputField
                      className="md:col-span-6 xl:col-span-6 px-0"
                      id={`${namespace}__customer_code-field`}
                      label="Customer Code"
                      placeholder="Type Customer Code"
                      {...props}
                    />
                  )}
                </Field>
                <div className="md:col-span-6 px-0" />
                <NetworkTypeaheadField curretNetworksIds={initialValues.networks} />
                <div className="md:col-span-6 px-0" />
                <div className="flex items-start mt-8 pb-9">
                  <Button
                    data-test-element="cancel-create-prd-btn"
                    label="Cancel"
                    onClick={onCancel}
                  />
                  <Button
                    className="ml-6"
                    data-test-element={isEditing ? 'update-prd-btn' : 'create-prd-btn'}
                    label={isEditing ? 'Update' : 'Create'}
                    primary
                    type="submit"
                  />
                </div>
              </form>
            )}
          />
        </div>
      )}
    </div>
  );
};

PrdForm.propTypes = {
  prdId: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  showPending: PropTypes.bool,
  isDeactivationEnabled: PropTypes.bool,
};

PrdForm.defaultProps = {
  prdId: '',
  initialValues: {},
  showPending: false,
  isDeactivationEnabled: false,
};

const mapStateToProps = (state) => ({
  isDeactivationEnabled: hint2340AllowUsersToDeactivateOldPrds(state),
});

export default connect(mapStateToProps)(PrdForm);
